import { useMemo } from 'react'
import { useUser } from '@fs/zion-user'
import useReactQuery from './useReactQuery'
import { getFamilyTreeData } from '../services/services'
import { withPortraitLinksForAll } from '../helpers/treePersonPortrait'

const EMPTY_LOCATIONS_LOOKUP = {}
const selectFamilyTreeData = (response) => response?.data

export const FAMILY_TREE_DATA_QUERY_KEY_PREFIX = 'family-tree-data-'

export const getFamilyTreeDataQueryKey = (personId, generations = 3, { includeLocations = false } = {}) => [
  `${FAMILY_TREE_DATA_QUERY_KEY_PREFIX}${personId}-generations-${generations}-include-locations-${includeLocations}`,
]

/** react-query filter predicate matching every cached family-tree query, whatever its depth or endpoint */
export const isFamilyTreeDataQuery = (query) => {
  const key = query?.queryKey?.[0]
  return typeof key === 'string' && key.startsWith(FAMILY_TREE_DATA_QUERY_KEY_PREFIX)
}

// The slim allaboutme endpoint answers with a bare persons array; the WICF endpoint wraps it as
// { persons, placesLookup }. Both land in the cache inside the raw axios response envelope.
const getFamilyTreePersons = (familyTreeData) => familyTreeData?.persons ?? familyTreeData

/**
 * Applies `updatePersons(persons) => persons` to a cached family-tree entry (the raw axios
 * response) while preserving whichever envelope the endpoint used. Returns the same entry when
 * the updater made no change, so react-query subscribers are not notified needlessly.
 */
export const patchFamilyTreeDataResponse = (cached, updatePersons) => {
  const payload = cached?.data
  const persons = getFamilyTreePersons(payload)
  if (!Array.isArray(persons)) return cached

  const nextPersons = updatePersons(persons)
  if (nextPersons === persons) return cached

  const nextPayload = Array.isArray(payload) ? nextPersons : { ...payload, persons: nextPersons }
  return { ...cached, data: nextPayload }
}

/**
 * Custom hook to fetch family tree data using React Query
 *
 * @param {number} generations - Number of generations to fetch (default: 3)
 * @returns {Object} Object containing:
 *   - data: The family tree data (persons array). Every person carries `portraitThumbnailLink` /
 *     `portraitImageLink` whichever endpoint answered (see shared/helpers/treePersonPortrait.js)
 *   - isLoading: Boolean indicating if data is being fetched
 *   - isError: Boolean indicating if request failed
 *   - error: Error object if request failed
 *   - isFetched: Boolean indicating if data has been fetched
 *   - refetch: Function to manually refetch data
 */
const useFamilyTreeData = (generations = 3, { includeLocations = false } = {}) => {
  const user = useUser()

  const queryKey = useMemo(
    () => getFamilyTreeDataQueryKey(user?.personId, generations, { includeLocations }),
    [includeLocations, user?.personId, generations]
  )

  const { query } = useReactQuery({
    queryFn: () => getFamilyTreeData(generations, { includeLocations }),
    queryKey,
    enabled: Boolean(user?.signedIn && user?.personId),
    staleTime: 600000, // 10 minutes
    select: selectFamilyTreeData,
  })

  const familyTreeData = query?.data

  // WICF names the portrait fields differently from allaboutme; normalize once here so
  // consumers (ancestor chips, detail cards, portrait upload) read one shape (TOGV2-1765)
  const data = useMemo(() => withPortraitLinksForAll(getFamilyTreePersons(familyTreeData)), [familyTreeData])

  return {
    data,
    locationsLookup: familyTreeData?.placesLookup ?? familyTreeData?.locationLookup ?? EMPTY_LOCATIONS_LOOKUP,
    isLoading: query?.isLoading,
    isError: query?.isError,
    error: query?.error,
    isFetched: query?.isFetched,
    refetch: query?.refetch,
  }
}

export default useFamilyTreeData
